import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { Container, Card } from 'react-bootstrap';
import { LuCircleCheck, LuPackageOpen, LuTruck, LuHouse, LuCircleAlert, LuLink } from 'react-icons/lu';
import { getShipmentByTrackingCode, clearPublicTracking } from '../../redux/reducers/shipmentSlice';
import ShipmentTimeline from './partials/ShipmentTimeline';
import ShipmentStatusBadge from './partials/ShipmentStatusBadge';

export default function PublicTrackingPage() {
    const { trackingCode } = useParams();
    const dispatch = useDispatch();

    const { publicTracking: shipment, loading, error } = useSelector(state => state.shipment);

    useEffect(() => {
        if (trackingCode) {
            dispatch(getShipmentByTrackingCode(trackingCode));
        }
        return () => { dispatch(clearPublicTracking()); }
    }, [dispatch, trackingCode]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100" style={{ background: '#f8fafc' }}>
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    if (error || !shipment) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100" style={{ background: '#f8fafc' }}>
                <Card className="border-0 shadow-sm p-5 text-center rounded-4" style={{ maxWidth: '400px' }}>
                    <LuCircleAlert size={48} className="text-danger mx-auto mb-3" />
                    <h5 className="fw-bold">Tracking Not Found</h5>
                    <p className="text-muted small mb-0">We could not find a shipment with tracking code <strong>{trackingCode}</strong>. Please check your link.</p>
                </Card>
            </div>
        );
    }

    // Determine visual phase (1 to 4)
    let phase = 1; // Pending / Processing
    if (shipment.status === 'DISPATCHED') phase = 2;
    if (shipment.status === 'OUT_FOR_DELIVERY') phase = 3;
    if (shipment.status === 'DELIVERED') phase = 4;
    // Special states
    const isFailed = ['FAILED', 'RETURNED', 'CANCELLED'].includes(shipment.status);

    const phases = [
        { label: 'Confirmed', icon: <LuCircleCheck size={24} /> },
        { label: 'Dispatched', icon: <LuPackageOpen size={24} /> },
        { label: 'Out for Delivery', icon: <LuTruck size={24} /> },
        { label: 'Delivered', icon: <LuHouse size={24} /> }
    ];

    return (
        <div className="min-vh-100" style={{ background: '#f1f5f9', padding: '40px 0' }}>
            <Container style={{ maxWidth: '600px' }}>
                
                <div className="text-center mb-5">
                    <h3 className="fw-bold text-dark mb-1">Track Your Shipment</h3>
                    <p className="text-muted">Enter the details below to view real-time updates.</p>
                </div>

                <Card className="border-0 shadow-lg rounded-4 mb-4 overflow-hidden">
                    <div className="p-4" style={{ background: 'linear-gradient(135deg, #28B0C4 0%, #1e8c9b 100%)', color: 'white' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <span className="text-uppercase fw-bold" style={{ fontSize: '11px', letterSpacing: '1px', opacity: 0.8 }}>
                                Tracking Code
                            </span>
                            <span className="rounded-pill px-3 py-1 fw-bold bg-white" style={{ fontSize: '11px', color: '#28B0C4' }}>
                                ETA: {moment(shipment.estimatedArrival?.current).format('MMM D, YYYY')}
                            </span>
                        </div>
                        <h2 className="fw-bold mb-3">{shipment.trackingCode}</h2>
                        
                        {!isFailed && (
                            <div className="position-relative mt-5 mb-2">
                                {/* Progress background line */}
                                <div className="position-absolute" style={{ top: '12px', left: '10%', right: '10%', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', zIndex: 0 }}></div>
                                
                                {/* Active line */}
                                <div className="position-absolute" style={{ top: '12px', left: '10%', width: `${((phase - 1) / 3) * 80}%`, height: '4px', background: '#fff', borderRadius: '2px', zIndex: 0, transition: 'width 0.5s ease' }}></div>

                                <div className="d-flex justify-content-between position-relative z-1">
                                    {phases.map((p, i) => (
                                        <div key={i} className="text-center" style={{ width: '60px' }}>
                                            <div 
                                                className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                                                style={{ 
                                                    width: '28px', height: '28px', 
                                                    background: i < phase ? '#fff' : 'rgba(255,255,255,0.2)', 
                                                    color: i < phase ? '#28B0C4' : 'rgba(255,255,255,0.6)',
                                                    boxShadow: i < phase ? '0 0 0 4px rgba(255,255,255,0.2)' : 'none'
                                                }}
                                            >
                                                {p.icon}
                                            </div>
                                            <div style={{ fontSize: '10px', fontWeight: 'bold', opacity: i < phase ? 1 : 0.6, whiteSpace: 'nowrap' }}>
                                                {p.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {isFailed && (
                            <div className="p-3 bg-danger rounded-3 mt-4 text-center">
                                <LuCircleAlert size={24} className="mb-2" />
                                <h6 className="fw-bold mb-0">Delivery Attempt Failed or Cancelled</h6>
                            </div>
                        )}
                    </div>
                </Card>

                {shipment.deliveryType === 'third_party' && shipment.thirdParty?.carrierTrackingUrl && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: '24px' }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="p-3 bg-light rounded-circle text-primary">
                                <LuLink size={24} />
                            </div>
                            <div>
                                <h6 className="fw-bold text-dark mb-1">Carrier Tracking Link</h6>
                                <a href={shipment.thirdParty.carrierTrackingUrl} target="_blank" rel="noreferrer" style={{ fontSize: '13px', color: '#28B0C4', fontWeight: '600', textDecoration: 'none' }}>
                                    Track via {shipment.thirdParty.carrierName} →
                                </a>
                            </div>
                        </div>
                    </Card>
                )}

                <Card className="border-0 shadow-sm rounded-4" style={{ padding: '24px' }}>
                    <h6 className="fw-bold text-dark mb-4">Detailed Timeline</h6>
                    <ShipmentTimeline timeline={shipment.timeline} />
                </Card>

            </Container>
        </div>
    );
}
